import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useB2BAccess } from './useB2BAccess';
import { type B2BModule, PLANS } from '@/config/plans';

interface VolumeAddon {
  id: string;
  addon_type: string;
  extra_units: number;
  is_active: boolean;
}

interface ModuleLimits {
  vehicles: { current: number; limit: number; extra: number; total: number; remaining: number; percentage: number };
  properties: { current: number; limit: number; extra: number; total: number; remaining: number; percentage: number };
  subcontractors: { current: number; limit: number; extra: number; total: number; remaining: number; percentage: number };
  tenders: { current: number; limit: number; extra: number; total: number; remaining: number; percentage: number };
}

interface ModuleLimitsResult {
  limits: ModuleLimits;
  canAdd: (module: B2BModule) => boolean;
  isNearLimit: (module: B2BModule, threshold?: number) => boolean;
  isAtLimit: (module: B2BModule) => boolean;
  getUsagePercentage: (module: B2BModule) => number;
  isLoading: boolean;
}

/**
 * Hook for tracking B2B module usage limits
 */
export function useModuleLimits(): ModuleLimitsResult {
  const { user } = useAuth();
  const { planId } = useB2BAccess();
  const plan = PLANS[planId] || PLANS.discovery;

  // Fetch current counts
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ['module-counts', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return { vehicles: 0, properties: 0, subcontractors: 0, tenders: 0 };
      }

      // Fetch all counts in parallel
      const [vehiclesRes, propertiesRes, subcontractorsRes, tendersRes] = await Promise.all([
        supabase.from('vehicles').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('properties').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('subcontractors').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('tenders').select('id', { count: 'exact', head: true }).eq('user_id', user.id).in('status', ['draft', 'in_progress', 'analyzing', 'go']),
      ]);

      return {
        vehicles: vehiclesRes.count || 0,
        properties: propertiesRes.count || 0,
        subcontractors: subcontractorsRes.count || 0,
        tenders: tendersRes.count || 0,
      };
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds - refresh more frequently for limit checks
  });

  // Fetch volume add-ons
  const { data: volumeAddons = [], isLoading: addonsLoading } = useQuery({
    queryKey: ['volume-addons', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('module_addons')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .in('addon_type', ['vehicles', 'properties', 'subcontractors', 'tenders']);

      if (error) {
        console.error('Error fetching volume addons:', error);
        return [];
      }

      return data as VolumeAddon[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate limits with add-ons
  const limits = useMemo((): ModuleLimits => {
    const baseVehicles = plan.limits.vehicles || 0;
    const baseProperties = plan.limits.properties || 0;
    const baseSubcontractors = plan.limits.subcontractors || 0;
    const baseTenders = plan.limits.tenders || 0;

    // Sum up extra units from add-ons
    const extraVehicles = volumeAddons
      .filter(a => a.addon_type === 'vehicles')
      .reduce((sum, a) => sum + (a.extra_units || 0), 0);
    const extraProperties = volumeAddons
      .filter(a => a.addon_type === 'properties')
      .reduce((sum, a) => sum + (a.extra_units || 0), 0);
    const extraSubcontractors = volumeAddons
      .filter(a => a.addon_type === 'subcontractors')
      .reduce((sum, a) => sum + (a.extra_units || 0), 0);
    const extraTenders = volumeAddons
      .filter(a => a.addon_type === 'tenders')
      .reduce((sum, a) => sum + (a.extra_units || 0), 0);

    const currentVehicles = counts?.vehicles || 0;
    const currentProperties = counts?.properties || 0;
    const currentSubcontractors = counts?.subcontractors || 0;
    const currentTenders = counts?.tenders || 0;

    const totalVehicles = baseVehicles === Infinity ? Infinity : baseVehicles + extraVehicles;
    const totalProperties = baseProperties === Infinity ? Infinity : baseProperties + extraProperties;
    const totalSubcontractors = baseSubcontractors === Infinity ? Infinity : baseSubcontractors + extraSubcontractors;
    const totalTenders = baseTenders === Infinity ? Infinity : baseTenders + extraTenders;

    const calcPercentage = (current: number, total: number) => 
      total === Infinity ? 0 : total === 0 ? 100 : Math.min(100, Math.round((current / total) * 100));

    return {
      vehicles: {
        current: currentVehicles,
        limit: baseVehicles,
        extra: extraVehicles,
        total: totalVehicles,
        remaining: totalVehicles === Infinity ? Infinity : Math.max(0, totalVehicles - currentVehicles),
        percentage: calcPercentage(currentVehicles, totalVehicles),
      },
      properties: {
        current: currentProperties,
        limit: baseProperties,
        extra: extraProperties,
        total: totalProperties,
        remaining: totalProperties === Infinity ? Infinity : Math.max(0, totalProperties - currentProperties),
        percentage: calcPercentage(currentProperties, totalProperties),
      },
      subcontractors: {
        current: currentSubcontractors,
        limit: baseSubcontractors,
        extra: extraSubcontractors,
        total: totalSubcontractors,
        remaining: totalSubcontractors === Infinity ? Infinity : Math.max(0, totalSubcontractors - currentSubcontractors),
        percentage: calcPercentage(currentSubcontractors, totalSubcontractors),
      },
      tenders: {
        current: currentTenders,
        limit: baseTenders,
        extra: extraTenders,
        total: totalTenders,
        remaining: totalTenders === Infinity ? Infinity : Math.max(0, totalTenders - currentTenders),
        percentage: calcPercentage(currentTenders, totalTenders),
      },
    };
  }, [plan, counts, volumeAddons]);

  // Helper functions
  const canAdd = (module: B2BModule): boolean => {
    const moduleLimit = limits[module === 'fleet' ? 'vehicles' : module];
    return moduleLimit.remaining > 0 || moduleLimit.total === Infinity;
  };

  const isNearLimit = (module: B2BModule, threshold = 80): boolean => {
    const moduleLimit = limits[module === 'fleet' ? 'vehicles' : module];
    return moduleLimit.percentage >= threshold && moduleLimit.total !== Infinity;
  };

  const isAtLimit = (module: B2BModule): boolean => {
    const moduleLimit = limits[module === 'fleet' ? 'vehicles' : module];
    return moduleLimit.remaining === 0 && moduleLimit.total !== Infinity;
  };

  const getUsagePercentage = (module: B2BModule): number => {
    const moduleLimit = limits[module === 'fleet' ? 'vehicles' : module];
    return moduleLimit.percentage;
  };

  return {
    limits,
    canAdd,
    isNearLimit,
    isAtLimit,
    getUsagePercentage,
    isLoading: countsLoading || addonsLoading,
  };
}
